import React, { useMemo, useState } from 'react';
import './BurdenChart.css';

const BurdenChart = ({ data, title }) => {
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const chartData = useMemo(() => {
    if (!data) return [];

    const lines = data.trim().split('\n');
    if (lines.length <= 1) return [];

    const headers = lines[0].split('\t');
    const dataLines = lines.slice(1);

    // Find the columns we need
    const labelIndex = headers.findIndex(h => 
      h.includes('CLNDN') || 
      h.includes('Burden') || 
      h === 'Gene' || 
      h === 'Disease' || 
      h === 'SYMBOL'
    );
    const countIndex = headers.findIndex(h => h.toLowerCase().includes('count'));
    
    if (labelIndex === -1 || countIndex === -1) return [];

    const chartItems = dataLines.map(line => {
      const cols = line.split('\t');
      return {
        label: cols[labelIndex]?.trim() || 'Unknown',
        count: parseInt(cols[countIndex]) || 0
      };
    }).filter(item => item.count > 0);

    // Sort by count descending and limit to top 15 to prevent overflow
    return chartItems.sort((a, b) => b.count - a.count).slice(0, 15);
  }, [data]);

  const maxCount = Math.max(...chartData.map(item => item.count), 1);

  const handleLabelMouseEnter = (item, event) => {
    if (item.label.length > 25) {
      setHoveredLabel(item.label);
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleLabelMouseMove = (event) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleLabelMouseLeave = () => {
    setHoveredLabel(null);
  };

  if (chartData.length === 0) {
    return (
      <div className="WSNG-burden-chart">
        <h4>{title}</h4>
        <div className="WSNG-no-data">No burden data available</div>
      </div>
    );
  }

  return (
    <div className="WSNG-burden-chart">
      <h4>{title}</h4>
      <div className="WSNG-chart-container">
        <div className="WSNG-chart-area">
          {chartData.map((item, index) => {
            const barWidth = (item.count / maxCount) * 100;
            
            return (
              <div key={index} className="WSNG-bar-row">
                <div 
                  className="WSNG-bar-label"
                  onMouseEnter={(e) => handleLabelMouseEnter(item, e)}
                  onMouseMove={handleLabelMouseMove}
                  onMouseLeave={handleLabelMouseLeave}
                >
                  {item.label.length > 25 ? item.label.substring(0, 22) + '...' : item.label}
                </div>
                <div className="WSNG-bar-track">
                  <div 
                    className="WSNG-bar"
                    style={{ width: `${barWidth}%` }}
                    title={`${item.label}: ${item.count} variants`}
                  >
                    <span className="WSNG-bar-value">{item.count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="WSNG-x-axis">
          <span className="WSNG-axis-label">Number of Variants</span>
        </div>
      </div>
      <div className="WSNG-chart-summary">
        <span>Total: {chartData.reduce((sum, item) => sum + item.count, 0)} variants</span>
        <span>Categories: {chartData.length}</span>
      </div>
      
      {/* Custom tooltip for full label names */}
      {hoveredLabel && (
        <div 
          className="WSNG-label-tooltip"
          style={{
            position: 'fixed',
            left: mousePosition.x + 10,
            top: mousePosition.y - 30,
            zIndex: 1000
          }}
        >
          {hoveredLabel}
        </div>
      )}
    </div>
  );
};

export default BurdenChart;
